import React, {createContext, useContext, useEffect, useMemo} from 'react';
import type {ClientId, ClientProfile, ClientFeatures, Theme, ModuleId} from '../client/types';
import {getClientProfile} from '../client/registry';
import {isClientId} from '../client/types';

const DEFAULT_FEATURES: ClientFeatures = {serialsEnabled: false, inventoryEnabled: false};

interface ClientContextValue {
  clientId: ClientId;
  profile: ClientProfile;
  theme: Theme;
}

const ClientContext = createContext<ClientContextValue | null>(null);

function getClientIdFromEnv (): ClientId {
  const id = typeof import.meta !== 'undefined' ? import.meta.env?.VITE_CLIENT_ID : undefined;
  return isClientId(id) ? id : 'identity';
}

export const ClientProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const value = useMemo(() => {
    const clientId = getClientIdFromEnv();
    const profile = getClientProfile(clientId);
    return {clientId, profile, theme: profile.theme};
  }, []);

  useEffect(() => {
    const themeClass = value.profile.theme.designSystem?.themeClass;
    if (themeClass) {
      document.documentElement.classList.add(themeClass);
    }
    document.title = value.profile.theme.appTitle;
    return () => {
      if (themeClass) {
        document.documentElement.classList.remove(themeClass);
      }
    };
  }, [value]);

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export function useClientContext (): ClientContextValue {
  const ctx = useContext(ClientContext);
  if (!ctx) {
    throw new Error('useClientContext must be used within ClientProvider');
  }
  return ctx;
}

export function useClientId (): ClientId {
  return useClientContext().clientId;
}

export function useClientProfile (): ClientProfile {
  return useClientContext().profile;
}

export function useClientTheme (): Theme {
  return useClientContext().theme;
}

/** Enabled feature module IDs for the current client (from profile). Use for route/nav filtering. */
export function useEnabledModules (): ModuleId[] {
  return useClientContext().profile.enabledModules;
}

/** Fine-grained feature flags for the current client. Safe to call — never throws. */
export function useClientFeatures (): ClientFeatures {
  return useClientContext().profile.features ?? DEFAULT_FEATURES;
}
